import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useRealtime } from '../../contexts/RealtimeContext';
import { 
  X, 
  ExternalLink, 
  Plus, 
  Filter, 
  Search, 
  Save, 
  Video, 
  Edit, 
  Trash2, 
  Calendar, 
  Clock, 
  Users,
  Play,
  Power
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { 
  saveLiveClass, 
  getLiveClasses, 
  updateLiveClass, 
  deleteLiveClass, 
  subscribeToLiveClasses
} from '../../firebase/collections';

const LiveClassManagement = () => {
  const { currentUser } = useRealtime();
  const navigate = useNavigate();
  const [liveClasses, setLiveClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterExam, setFilterExam] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    subject: '',
    exam: 'CAT',
    instructorName: '',
    scheduledAt: '',
    duration: 60,
    maxParticipants: 50,
    instructorId: '',
    price: 0,
    status: 'scheduled',
    meetingType: 'internal', // 'internal' (built-in) or 'external' (Zoom/Meet)
    meetingLink: '' // Zoom/Meet link (only required for external)
  });

  const exams = ['CAT', 'JEE', 'NEET', 'GATE', 'UPSC', 'Banking'];
  const subjects = ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'English', 'General Knowledge', 'Reasoning'];
  const statuses = ['scheduled', 'live', 'completed', 'cancelled'];

  useEffect(() => {
    loadLiveClasses();
    const unsubscribe = subscribeToLiveClasses((result) => {
      if (result.success) {
        setLiveClasses(result.data);
      }
    });
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const loadLiveClasses = async () => {
    try {
      setLoading(true);
      const result = await getLiveClasses();
      if (result.success) {
        setLiveClasses(result.data);
      } else {
        setError('Failed to load live classes');
      }
    } catch (error) {
      setError('Failed to load live classes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!formData.title.trim() || !formData.scheduledAt) {
        setError('Title and scheduled date are required');
        return;
      }

      // Only require meeting link for external meetings
      if (formData.meetingType === 'external' && (!formData.meetingLink || !formData.meetingLink.trim())) {
        setError('Meeting link (Zoom/Meet/Teams) is required for external meetings');
        return;
      }

      const liveClassData = {
        ...formData,
        scheduledAt: new Date(formData.scheduledAt).toISOString(),
        price: parseFloat(formData.price) || 0,
        duration: parseInt(formData.duration) || 60,
        maxParticipants: parseInt(formData.maxParticipants) || 50,
        instructorId: formData.instructorId || currentUser?.uid || '',
        createdBy: currentUser?.uid || ''
      };

      if (editingClass) {
        const result = await updateLiveClass(editingClass.id, liveClassData);
        if (result.success) {
          setSuccess('Live class updated successfully');
          setShowAddModal(false);
          setEditingClass(null);
        } else {
          setError('Failed to update live class');
        }
      } else {
        const result = await saveLiveClass(liveClassData);
        if (result.success) {
          setSuccess('Live class created successfully');
          setShowAddModal(false);
        } else {
          setError('Failed to create live class');
        }
      }
    } catch (error) {
      setError('Failed to save live class');
    }
  };

  const handleDelete = async (classId) => {
    if (window.confirm('Are you sure you want to delete this live class?')) {
      try {
        const result = await deleteLiveClass(classId);
        if (result.success) {
          setSuccess('Live class deleted successfully');
          loadLiveClasses();
        } else {
          setError('Failed to delete live class');
        }
      } catch (error) {
        setError('Failed to delete live class');
      }
    }
  };

  const handleStartClass = async (liveClass) => {
    try {
      const result = await updateLiveClass(liveClass.id, {
        ...liveClass,
        status: 'live'
      });
      if (result.success) {
        setSuccess('Live class started');
        loadLiveClasses();
      } else {
        setError('Failed to start live class');
      }
    } catch (error) {
      setError('Failed to start live class');
    }
  };

  const handleJoinClass = (liveClass) => {
    // If meetingType is 'internal' or no meetingLink, use built-in interface
    if (liveClass.meetingType === 'internal' || !liveClass.meetingLink) {
      navigate(`/live-class/${liveClass.id}`);
    } else if (liveClass.meetingLink) {
      // Open external meeting link (Zoom/Meet) in new tab
      window.open(liveClass.meetingLink, '_blank', 'noopener,noreferrer');
    }
  };

  const canJoinClass = (liveClass) => {
    const now = new Date();
    const scheduledAt = liveClass.scheduledAt ? new Date(liveClass.scheduledAt) : null;
    const duration = liveClass.duration || 60;
    const endTime = scheduledAt ? new Date(scheduledAt.getTime() + duration * 60000) : null;

    if (liveClass.status === 'live') return true;
    if (liveClass.status === 'scheduled' && scheduledAt) {
      // Allow joining 5 minutes before scheduled time
      const joinTime = new Date(scheduledAt.getTime() - 5 * 60000);
      return now >= joinTime && now <= endTime;
    }
    return false;
  };

  const handleEdit = (liveClass) => {
    setEditingClass(liveClass);
    setFormData({
      ...liveClass,
      scheduledAt: new Date(liveClass.scheduledAt).toISOString().slice(0, 16)
    });
    setShowAddModal(true);
  };

  const filteredClasses = liveClasses.filter(liveClass => {
    const matchesSearch = liveClass.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         liveClass.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || liveClass.status === filterStatus;
    const matchesExam = !filterExam || liveClass.exam === filterExam;
    
    return matchesSearch && matchesStatus && matchesExam;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'live': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (showAddModal) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {editingClass ? 'Edit Live Class' : 'Create New Live Class'}
          </h1>
          <button
            onClick={() => {
              setShowAddModal(false);
              setEditingClass(null);
            }}
            className="text-gray-600 hover:text-gray-800"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
              <select
                value={formData.subject}
                onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {subjects.map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Exam</label>
              <select
                value={formData.exam}
                onChange={(e) => setFormData(prev => ({ ...prev, exam: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {exams.map(exam => (
                  <option key={exam} value={exam}>{exam}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Instructor Name</label>
              <input
                type="text"
                value={formData.instructorName}
                onChange={(e) => setFormData(prev => ({ ...prev, instructorName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Scheduled Date & Time *</label>
              <input
                type="datetime-local"
                value={formData.scheduledAt}
                onChange={(e) => setFormData(prev => ({ ...prev, scheduledAt: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Duration (minutes)</label>
              <input
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="15"
                max="480"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Max Participants</label>
              <input
                type="number"
                value={formData.maxParticipants}
                onChange={(e) => setFormData(prev => ({ ...prev, maxParticipants: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="1"
                max="1000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Meeting Type *</label>
              <select
                value={formData.meetingType}
                onChange={(e) => setFormData(prev => ({ ...prev, meetingType: e.target.value, meetingLink: e.target.value === 'internal' ? '' : prev.meetingLink }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="internal">Built-in Interface (WebRTC)</option>
                <option value="external">External (Zoom/Meet/Teams Link)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {formData.meetingType === 'internal' 
                  ? 'Uses built-in video/audio interface - no external link needed'
                  : 'Requires a Zoom / Google Meet / Microsoft Teams link'}
              </p>
            </div>

            {formData.meetingType === 'external' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Meeting Link (Zoom/Meet/Teams) *</label>
                <input
                  type="url"
                  value={formData.meetingLink}
                  onChange={(e) => setFormData(prev => ({ ...prev, meetingLink: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://zoom.us/j/... or https://meet.google.com/... or https://teams.microsoft.com/..."
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the Zoom, Google Meet, or Microsoft Teams link for this class
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Price (₹)</label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => {
                setShowAddModal(false);
                setEditingClass(null);
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
            >
              <Save size={16} className="mr-2" />
              {editingClass ? 'Update Class' : 'Create Class'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Live Class Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage live classes, schedules, and meeting links
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary inline-flex items-center"
          >
            <Plus size={16} className="mr-2" />
            Add Live Class
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Filter size={16} className="mr-2" /> Filters
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Search classes..."
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              {statuses.map(status => (
                <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Exam</label>
            <select
              value={filterExam}
              onChange={(e) => setFilterExam(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Exams</option>
              {exams.map(exam => (
                <option key={exam} value={exam}>{exam}</option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('');
                setFilterExam('');
              }}
              className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Live Classes List */}
      {loading ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-8 text-center"
        >
          <Video className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Loading Live Classes...
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Please wait while we fetch the available live classes.
          </p>
        </motion.div>
      ) : filteredClasses.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-8 text-center"
        >
          <Video className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {liveClasses.length === 0 ? 'No Live Classes Created Yet' : 'No Classes Match Filters'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {liveClasses.length === 0 
              ? 'Create your first live class with meeting links and schedules.'
              : 'Try adjusting your search criteria or filters.'
            }
          </p>
          {liveClasses.length === 0 && (
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary inline-flex items-center"
            >
              <Plus size={16} className="mr-2" />
              Create First Live Class
            </button>
          )}
        </motion.div>
      ) : (
        <div className="grid gap-6">
          {filteredClasses.map((liveClass, index) => (
            <motion.div
              key={liveClass.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="card p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {liveClass.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {liveClass.subject} • {liveClass.exam} • {liveClass.instructorName}
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(liveClass)}
                    className="btn-secondary inline-flex items-center"
                  >
                    <Edit size={16} className="mr-2" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(liveClass.id)}
                    className="btn-danger inline-flex items-center"
                  >
                    <Trash2 size={16} className="mr-2" />
                    Delete
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="flex items-center space-x-2">
                  <Calendar size={16} className="text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {formatDateTime(liveClass.scheduledAt)}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock size={16} className="text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {liveClass.duration} minutes
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users size={16} className="text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {liveClass.participantCount || 0}/{liveClass.maxParticipants} participants
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                <span className={`px-2 py-1 text-xs rounded ${getStatusColor(liveClass.status)}`}>
                  {liveClass.status.charAt(0).toUpperCase() + liveClass.status.slice(1)}
                </span>
                <span className={`px-2 py-1 text-xs rounded ${
                  liveClass.meetingType === 'internal' 
                    ? 'bg-purple-100 text-purple-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {liveClass.meetingType === 'internal' ? 'Built-in' : 'External'}
                </span>
                {liveClass.price > 0 && (
                  <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded">
                    ₹{liveClass.price}
                  </span>
                )}
                {(liveClass.status === 'live' || liveClass.status === 'scheduled') && liveClass.meetingType === 'external' && liveClass.meetingLink && (
                  <a
                    href={liveClass.meetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded hover:bg-blue-200 flex items-center"
                  >
                    <ExternalLink size={12} className="mr-1" />
                    {liveClass.status === 'live' ? 'Join Meeting' : 'View Meeting Link'}
                  </a>
                )}
                {(liveClass.status === 'live' || liveClass.status === 'scheduled') && liveClass.meetingType === 'internal' && (
                  <div className="flex items-center space-x-2">
                    {liveClass.status === 'scheduled' && (
                      <button
                        onClick={() => handleStartClass(liveClass)}
                        className="bg-green-600 text-white text-xs px-3 py-1 rounded hover:bg-green-700 flex items-center space-x-1 transition-colors"
                        title="Start the live class"
                      >
                        <Power size={12} />
                        <span>Start Class</span>
                      </button>
                    )}
                    {(liveClass.status === 'live' || canJoinClass(liveClass)) && (
                      <button
                        onClick={() => handleJoinClass(liveClass)}
                        className="bg-blue-600 text-white text-xs px-3 py-1 rounded hover:bg-blue-700 flex items-center space-x-1 transition-colors"
                        title="Join the live class"
                      >
                        <Play size={12} />
                        <span>Join Class</span>
                      </button>
                    )}
                    {liveClass.status === 'scheduled' && !canJoinClass(liveClass) && (
                      <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                        Scheduled
                      </span>
                    )}
                  </div>
                )}
              </div>

              {liveClass.description && (
                <p className="text-gray-600 text-sm">
                  {liveClass.description}
                </p>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LiveClassManagement;
